import { AxiosError } from "axios";
import { QueryFunctionContext, useQuery, UseQueryOptions } from "react-query";

import useCurrentEnvironment from "@/hooks/use-environment";
import { useDeepMemo } from "@/hooks/useDeepMemo";
import { queryClient } from "@/queryClient";
import { LoadedEventDefinition } from "@/types/global";
import { makeRequest } from "@/util";

import * as adPerformance from "./ad-performance";
import * as aiChat from "./ai-chat";
import * as aiChatApproval from "./ai-chat-approval";
import * as costSavings from "./cost-savings";
import * as funnelsApi from "./funnels";
import * as app from "./app";
import * as audiences from "./audiences";
import * as auditLog from "./auditlog";
import * as consentManagement from "./consent-management";
import * as events from "./events";
import * as insights from "./insights";
import * as product from "./product";
import * as profiles from "./profiles";
import * as customerJourneys from "./customer-journeys";
import * as surveys from "./surveys";
import * as systemStatus from "./systemStatus";

export * from "./customer-journeys";
export * from "./ad-performance";
export * from "./ai-chat";
export * from "./cost-savings";
export * from "./funnels";
export * from "./app";
export * from "./audiences";
export * from "./auditlog";
export * from "./consent-management";
export * from "./events";
export * from "./insights";
export * from "./product";
export * from "./profiles";
export * from "./surveys";
export * from "./systemStatus";

/*
 * Update these mappings with the appropriate types to get a typed API.
 * The keynames should match the operation names in the backend API.
 * */
interface ApiTypingsLookup {
  admin_list_destinations: {
    request: app.AdminListDestinationsRequest;
    response: app.AdminListDestinationsResponse;
  };
  get_audience_list: {
    request: audiences.AudienceListRequest;
    response: audiences.AudienceListResponse;
  };
  get_audience_destination_capabilities: {
    request: {};
    response: audiences.GetAudienceDestinationCapabilitiesResponse;
  };
  get_event_count_summary: {
    request: events.EventCountSummaryRequest;
    response: events.EventCountSummaryResponse;
  };
  get_user_activity: {
    request: {};
    response: app.UserActivityData;
  };
  list_active_destinations: {
    request: app.ListActiveDestinationsRequest;
    response: app.ListActiveDestinationsResponse;
  };
  list_all_destinations_for_env: {
    request: {};
    response: app.ListAllDestinationsForEnvResponse;
  };
  rank_destinations_for_customers: {
    request: product.RankDestinationsRequest;
    response: product.RankDestinationsResponse;
  };
  get_total_conversions: {
    request: {};
    response: insights.TotalConversionsResponse;
  };
  get_source_performance: {
    request: insights.SourceRequest;
    response: insights.SourcePerformanceResponse;
  };
  get_source_analysis: {
    request: insights.SourceRequest;
    response: insights.SourceAnalysisResponse[];
  };
  get_insights_overview: {
    request: insights.DateRangeRequest;
    response: insights.InsightsOverviewResponse;
  };
  get_dynamic_events2: {
    request: {};
    response: insights.DynamicEventsResponse;
  };
  get_integration_stats_pull_status: {
    request: insights.IntegrationStatsPullRequest;
    response: insights.IntegrationStatsPullResponse;
  };
  get_env_config: {
    request: Record<string, never>;
    response: insights.GetEnvConfigResponse;
  };
  get_event_def: {
    request: { event_def_id: string };
    response: LoadedEventDefinition | null;
  };
  get_recommendation_cards: {
    request: insights.DateRangeRequest;
    response: insights.RecommendationCardsResponse[];
  };
  get_event_defs_enriched: {
    request: {};
    response: insights.FunnelEvents[];
  };
  analytics_funnel_query: {
    request: insights.FunnelQueryRequest;
    response: insights.FunnelStepResult[];
  };
  get_consent_management_config_versions: {
    request: consentManagement.GetConfigVersionsRequest;
    response: consentManagement.GetConfigVersionsResponse;
  };
  get_consent_management_configs: {
    request: {};
    response: consentManagement.GetConsentManagementConfigsResponse;
  };
  get_consent_metrics_summary: {
    request: consentManagement.ConsentMetricsSummaryRequest;
    response: consentManagement.ConsentMetricsSummaryResponse;
  };
  get_consent_metrics_breakdown: {
    request: consentManagement.ConsentMetricsBreakdownRequest;
    response: consentManagement.ConsentMetricsBreakdownResponse;
  };
  get_hipaa_allowlist_auditlog_json: {
    request: auditLog.AuditLogQueryParams;
    response: auditLog.AuditLogResponse;
  };
  admin_get_offline_attributions_customer: {
    request: {};
    response: adPerformance.GetOfflineAttributionsCustomerResponse;
  };
  funnel_query: {
    request: funnelsApi.PageViewFunnelQueryRequest;
    response: funnelsApi.FunnelStepResult[];
  };
  list_funnels: {
    request: Record<string, never>;
    response: funnelsApi.ListFunnelsResponse;
  };
  create_update_funnel: {
    request: funnelsApi.Funnel;
    response: funnelsApi.Funnel;
  };
  delete_funnel: {
    request: { id: string };
    response: string;
  };
  get_surveys: {
    request: surveys.GetSurveysRequest;
    response: surveys.GetSurveysResponse;
  };
  get_survey: {
    request: surveys.GetSurveyRequest;
    response: surveys.GetSurveyResponse;
  };
  get_survey_results: {
    request: surveys.GetSurveyResultsRequest;
    response: surveys.GetSurveyResultsResponse;
  };
  get_survey_timeseries: {
    request: surveys.GetSurveyTimeseriesRequest;
    response: surveys.GetSurveyTimeseriesResponse;
  };
  get_survey_responses: {
    request: surveys.GetSurveyResponsesRequest;
    response: surveys.GetSurveyResponsesResponse;
  };
  ai_chat_list_conversations: {
    request: aiChat.ListConversationsRequest;
    response: aiChat.ListConversationsResponse;
  };
  ai_chat_get_conversation: {
    request: aiChat.GetConversationRequest;
    response: aiChat.GetConversationResponse;
  };
  ai_chat_save_conversation: {
    request: aiChat.SaveConversationRequest;
    response: aiChat.SaveConversationResponse;
  };
  ai_chat_get_approval_status: {
    request: aiChatApproval.GetApprovalStatusRequest;
    response: aiChatApproval.GetApprovalStatusResponse;
  };
  get_system_status_wtm: {
    request: Record<string, never>;
    response: systemStatus.SystemStatusWTMSummaryResponse;
  };
  get_system_status_wtm_history: {
    request: systemStatus.SystemStatusWTMHistoryRequest;
    response: systemStatus.SystemStatusWTMHistoryResponse;
  };
  get_system_status_wtm_export: {
    request: Record<string, never>;
    response: string;
  };
  search_profiles: {
    request: profiles.SearchProfilesRequest;
    response: profiles.SearchProfilesResponse;
  };
  get_profile: {
    request: profiles.GetProfileRequest;
    response: profiles.GetProfileResponse;
  };
  get_profile_events: {
    request: profiles.GetProfileEventsRequest;
    response: profiles.GetProfileEventsResponse;
  };
  get_profile_session_events: {
    request: profiles.GetProfileSessionEventsRequest;
    response: profiles.GetProfileSessionEventsResponse;
  };
  get_profile_filter_options: {
    request: Record<string, never>;
    response: profiles.ProfileFilterOptionsResponse;
  };
  get_cost_metrics_timeseries: {
    request: costSavings.CostMetricsRequest;
    response: costSavings.CostMetricsResponse;
  };
  get_journey_sankey: {
    request: customerJourneys.JourneySankeyRequest;
    response: customerJourneys.JourneySankeyResponse;
  };
  get_top_journeys: {
    request: customerJourneys.TopJourneysRequest;
    response: customerJourneys.TopJourneysResponse;
  };
  get_channel_contribution: {
    request: customerJourneys.ChannelJourneyRequest;
    response: customerJourneys.ChannelContributionResponse;
  };
}

/**
 * Typed version of useQuery. It's meant to provide a 1-1 mapping between the backend api and the client cache.
 */
export function useApiQuery<OperationName extends keyof ApiTypingsLookup>(
  queryKey: PublicQueryKey<OperationName>,
  options?: Omit<
    UseQueryOptions<
      ApiTypingsLookup[OperationName]["request"],
      AxiosError,
      ApiTypingsLookup[OperationName]["response"],
      PrivateQueryKey<OperationName>
    >,
    "queryKey" | "queryFn"
  >,
) {
  const environment = useCurrentEnvironment();
  const stableQueryKey = useDeepMemo<PrivateQueryKey<OperationName>>([
    ...queryKey,
    environment?.ID || "",
  ]);

  return useQuery(stableQueryKey, makeRequestFetcher, options);
}

/**
 * Typed function to invalidate a query cache key. It just ensures that we're talking about the same query keys.
 */
export function invalidateQuery<OperationName extends keyof ApiTypingsLookup>(
  queryKey: Partial<PublicQueryKey<OperationName>>,
) {
  return queryClient.invalidateQueries({ queryKey });
}

/**
 * Typed function to update a query cache key. It just ensures that we're talking about the same query keys.
 */
export function updateQuery<OperationName extends keyof ApiTypingsLookup>(
  queryKey: Partial<PublicQueryKey<OperationName>>,
  payload:
    | ApiTypingsLookup[OperationName]["response"]
    | ((
        existingData?: ApiTypingsLookup[OperationName]["response"] | null,
      ) => ApiTypingsLookup[OperationName]["response"] | null),
) {
  return queryClient.setQueriesData(queryKey, payload);
}

/**
 * Everything below here is implementation details.
 */

type ID = string;

type PublicQueryKey<OperationName extends keyof ApiTypingsLookup> =
  | [OperationName, ApiTypingsLookup[OperationName]["request"]]
  | [OperationName, ID, ApiTypingsLookup[OperationName]["request"]];

/**
 * Append envID so that queries are scoped to the current environment.
 */
type EnvID = string;
type PrivateQueryKey<OperationName extends keyof ApiTypingsLookup> =
  | [OperationName, ApiTypingsLookup[OperationName]["request"], EnvID]
  | [OperationName, ID, ApiTypingsLookup[OperationName]["request"], EnvID];

function unpackQueryKey<OperationName extends keyof ApiTypingsLookup>(
  queryKey: PrivateQueryKey<OperationName>,
): [OperationName, ApiTypingsLookup[OperationName]["request"]] {
  /*
   * We want to allow for specifiers in the query's cache key for situations like IDs or categories
   * which are less ergonomic to invalidate via the params key.
   * We still are only using the kind + params for the request.
   */
  if (queryKey.length === 4) {
    const [kind, _id, params, _envID] = queryKey;
    return [kind, params];
  }

  const [kind, params, _envID] = queryKey;
  return [kind, params];
}

async function makeRequestFetcher<OperationName extends keyof ApiTypingsLookup>(
  ctx: QueryFunctionContext<PrivateQueryKey<OperationName>>,
) {
  const [kind, params] = unpackQueryKey(ctx.queryKey);

  const { data } = await makeRequest<ApiTypingsLookup[OperationName]>(
    kind,
    params,
  );
  return data;
}
